import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { editionsAPI, eventsAPI } from '../../lib/api';
import { Calendar, MapPin, ArrowRight, Rocket } from 'lucide-react';
import { useSEO } from '../../hooks/useSEO';
import { useIsMobile } from '../../hooks/useIsMobile';

// Colore primario viola per l'edizione corrente (stesso del tema)
const PRIMARY_COLOR = '#9333EA';
const NEUTRAL_COLOR = '#6B7280';

export const EditionsPage = () => {
  useSEO('Edizioni');
  const isMobile = useIsMobile();
  const [editions, setEditions] = useState([]);
  const [eventsMap, setEventsMap] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const res = await editionsAPI.getAll('publish');
      setEditions(res.data);
      
      const eventsData = {};
      for (const edition of res.data) {
        const eventsRes = await eventsAPI.getAll({ edition_id: edition.id, status: 'publish' });
        eventsData[edition.id] = eventsRes.data;
      }
      setEventsMap(eventsData);
    } catch (error) {
      console.error('Error loading editions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFullUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse-glow w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
          <Rocket className="text-primary animate-bounce" size={32} />
        </div>
      </div>
    );
  }

  return (
    <div className="py-16" data-testid="editions-page">
      {/* Header */}
      <div className="text-center mb-20 px-6">
        <p className="text-xs font-mono uppercase tracking-[0.3em] text-primary mb-4">
          Tutte le Edizioni
        </p>
        <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
          Edizioni SIOS
        </h1>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          Scopri tutte le edizioni di StartupItalia Open Summit, 
          con le tappe in tutta Italia.
        </p>
      </div>

      {/* Editions List - Full width with distinct sections */}
      <div className="space-y-0">
        {editions.map((edition, index) => {
          // Solo l'anno corrente è evidenziato con viola
          const currentYear = new Date().getFullYear();
          const isCurrentYear = edition.year === currentYear || edition.year === currentYear + 1;
          const accentColor = isCurrentYear ? PRIMARY_COLOR : NEUTRAL_COLOR;
          
          return (
            <section 
              key={edition.id} 
              data-testid={`edition-${edition.id}`}
              className="relative"
            >
              {/* Top border separator - visible divider between editions */}
              {index > 0 && (
                <div className="h-px w-full bg-gradient-to-r from-transparent via-white/20 to-transparent" />
              )}
              
              {/* Edition Container */}
              <div 
                className="relative py-16 md:py-24"
                style={{ 
                  borderLeft: isCurrentYear ? `4px solid ${accentColor}` : '4px solid transparent',
                  background: isCurrentYear ? `linear-gradient(135deg, ${accentColor}08 0%, transparent 50%)` : 'transparent'
                }}
              >
                <div className="max-w-6xl mx-auto px-6 md:px-12">
                  {/* Edition Header */}
                  <div className="mb-12">
                    {/* Year Badge */}
                    <div className="flex items-center gap-4 mb-6">
                      {isCurrentYear ? (
                        <div 
                          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-heading text-xl font-bold"
                          style={{ 
                            backgroundColor: accentColor,
                            color: '#000'
                          }}
                        >
                          <Calendar size={22} />
                          {edition.year}
                        </div>
                      ) : (
                        <div 
                          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-heading text-xl font-bold border border-white/20 text-white/60"
                        >
                          <Calendar size={22} />
                          {edition.year}
                        </div>
                      )}
                      <span 
                        className="text-sm font-mono px-4 py-2 rounded-full border"
                        style={{ 
                          borderColor: isCurrentYear ? `${accentColor}50` : 'rgba(255,255,255,0.2)',
                          color: isCurrentYear ? accentColor : 'rgba(255,255,255,0.5)'
                        }}
                      >
                        {eventsMap[edition.id]?.length || 0} eventi
                      </span>
                    </div>
                    
                    {/* Edition Title */}
                    <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
                      {edition.title}
                    </h2>
                    
                    {edition.description && (
                      <p className="text-muted-foreground max-w-3xl text-lg leading-relaxed">
                        {edition.description}
                      </p>
                    )}
                  </div>

                  {/* Events Grid */}
                  {eventsMap[edition.id]?.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {eventsMap[edition.id].map((event) => {
                        const heroUrl = event.hero_image 
                          ? getFullUrl(event.hero_image)
                          : event.gallery?.[0] || null;
                        const cardImg = isMobile && event.hero_image_mobile
                          ? getFullUrl(event.hero_image_mobile)
                          : heroUrl;
                        
                        return (
                          <Link 
                            key={event.id} 
                            to={`/events/${event.slug}`}
                            className="group block"
                            data-testid={`event-card-${event.id}`}
                          >
                            <article 
                              className="h-full rounded-2xl overflow-hidden bg-black/40 backdrop-blur-sm transition-all duration-300 hover:transform hover:scale-[1.02]"
                              style={{ 
                                border: isCurrentYear ? `2px solid ${accentColor}30` : '2px solid rgba(255,255,255,0.1)',
                              }}
                            >
                              {/* Image */}
                              <div className="relative aspect-[16/10] overflow-hidden">
                                {cardImg ? (
                                  <img 
                                    src={cardImg} 
                                    alt={event.title}
                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                  />
                                ) : (
                                  <div 
                                    className="w-full h-full flex items-center justify-center bg-gradient-to-br from-white/10 to-white/5"
                                  >
                                    <MapPin className="text-white/30" size={48} />
                                  </div>
                                )}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                                
                                {/* Date badge */}
                                {event.date && (
                                  <div className="absolute top-3 right-3">
                                    <span 
                                      className="text-xs font-mono text-white px-3 py-1.5 rounded-full font-semibold"
                                      style={{ backgroundColor: isCurrentYear ? accentColor : 'rgba(255,255,255,0.2)' }}
                                    >
                                      {event.date}
                                    </span>
                                  </div>
                                )}

                                {/* Location badge */}
                                <div className="absolute bottom-3 left-3">
                                  <span className="text-xs font-mono uppercase tracking-wider text-white bg-black/60 backdrop-blur-sm px-3 py-1.5 rounded-full flex items-center gap-1.5">
                                    <MapPin size={12} />
                                    {event.location}
                                  </span>
                                </div>
                              </div>

                              {/* Content */}
                              <div className="p-5 space-y-3">
                                <h3 className="font-heading text-lg font-semibold group-hover:text-primary transition-colors line-clamp-2">
                                  {event.title}
                                </h3>

                                {event.description && (
                                  <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">
                                    {event.description}
                                  </p>
                                )}

                                <div 
                                  className="flex items-center text-sm font-medium pt-2"
                                  style={{ color: isCurrentYear ? accentColor : 'rgba(255,255,255,0.6)' }}
                                >
                                  <span className="group-hover:mr-2 transition-all">Scopri</span>
                                  <ArrowRight size={14} className="opacity-0 -ml-4 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                                </div>
                              </div>
                            </article>
                          </Link>
                        );
                      })}
                    </div>
                  ) : (
                    <div 
                      className="text-center py-16 rounded-2xl bg-black/20"
                      style={{ border: isCurrentYear ? `2px dashed ${accentColor}30` : '2px dashed rgba(255,255,255,0.1)' }}
                    >
                      <MapPin className="mx-auto text-muted-foreground/50 mb-4" size={40} />
                      <p className="text-muted-foreground">
                        Nessun evento pubblicato per questa edizione
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </section>
          );
        })}
      </div>

      {editions.length === 0 && (
        <div className="text-center py-24 px-6">
          <Calendar className="mx-auto text-muted-foreground/50 mb-6" size={56} />
          <h2 className="font-heading text-2xl font-semibold mb-3">
            Nessuna edizione disponibile
          </h2>
          <p className="text-muted-foreground">
            Le edizioni verranno pubblicate presto.
          </p>
        </div>
      )}
    </div>
  );
};
